import { Loader2, Mail } from "lucide-react";
import React, { useCallback, useState } from "react";
import { usePostV4ManufacturerByRouteManufacturerIdDocumentAndDocumentNumberEmailMutation } from "randmar-api-client";
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Textarea,
  useToast,
} from "randmarcomps";
import { useManufacturer } from "../../../contexts/WithManufacturer";

interface IProps {
  documentNumber: string;
  children: React.ReactNode;
  emailAddress?: string;
}

const SendByEmailWrapper: React.FC<IProps> = ({ documentNumber, children, emailAddress }) => {
  const { applicationId } = useManufacturer();
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState(emailAddress || "");
  const [comment, setComment] = useState("");
  const { toast } = useToast();
  const [sendEmail, { isLoading }] = usePostV4ManufacturerByRouteManufacturerIdDocumentAndDocumentNumberEmailMutation();

  const handleCancel = useCallback(() => {
    setOpen(false);
  }, []);

  const handleButtonClicked = useCallback(() => {
    setEmail(emailAddress || "");
    setComment("");
    setOpen(true);
  }, [emailAddress]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        handleButtonClicked();
      }
    },
    [handleButtonClicked]
  );

  const handleConfirm = useCallback(async () => {
    try {
      await sendEmail({
        routeManufacturerId: applicationId,
        documentNumber,
        emailAddress: email,
        comment,
      }).unwrap();
      toast({ variant: "success", title: "Success", description: "Email successfully sent." });
    } catch (err) {
      toast({ variant: "destructive", title: "Error", description: "Could not send email." });
    }
    setOpen(false);
  }, [applicationId, comment, documentNumber, email, sendEmail, toast]);

  return (
    <>
      <div onClick={handleButtonClicked} onKeyDown={handleKeyDown} role="button" tabIndex={0}>
        {children}
      </div>
      <Dialog open={open} onOpenChange={(isOpen) => !isLoading && setOpen(isOpen)}>
        <DialogContent
          onInteractOutside={(e) => {
            if (isLoading) e.preventDefault();
          }}
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="h-5 w-5" />
              Send by Email
            </DialogTitle>
            <DialogDescription>
              The document will be sent as an attachment to the email address you provide.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid w-full items-center gap-1.5">
              <Label htmlFor="email">Email</Label>
              <Input
                type="email"
                id="email"
                placeholder="email@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoFocus
                required
                disabled={isLoading}
              />
            </div>
            <div className="grid w-full items-center gap-1.5">
              <Label htmlFor="comment">Comment (optional)</Label>
              <Textarea
                placeholder="Type your comment here."
                id="comment"
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                disabled={isLoading}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={handleCancel} disabled={isLoading}>
              Cancel
            </Button>
            <Button onClick={handleConfirm} disabled={!email.includes("@") || isLoading}>
              {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Confirm
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default SendByEmailWrapper;
